import { useSmsVerificationBlocked } from '@/hooks/useSmsVerificationBlocked';
import { Typography } from '@montugroup/design-system';
import { Box, styled } from '@mui/material';

type ResendSmsInfoProps = {
  dialogInfoIndex: number;
  sendSmsRetryCount: number;
  setSendSmsRetryCount: (count: number) => void;
};

const ResendSmsInfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
}));

const ResendInfoHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(4.5),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(4),
}));

const StyledTypography = styled(Typography)(() => ({
  lineHeight: 1.5,
}));

const InitialRetryInfo = () => (
  <>
    <ResendInfoHeading>Didn&apos;t receive an SMS verification?</ResendInfoHeading>
    <StyledTypography>
      There may be a short delay with your mobile provider. You can try resending the SMS.
    </StyledTypography>
    <StyledTypography>
      If you are an <b>existing patient</b>, we may already have a phone number linked to your profile. For privacy and
      compliance reasons, we are required to use the number on file.
    </StyledTypography>
    <StyledTypography>
      If your number has changed, please book a consultation with a nurse to update your personal details.
    </StyledTypography>
  </>
);

const SecondRetryInfo = () => (
  <>
    <ResendInfoHeading>We&apos;ve resent the SMS</ResendInfoHeading>
    <StyledTypography>
      If you still haven’t received a code after waiting a minute, check the following before trying again:
    </StyledTypography>
    <Box>
      <Typography component={'ul'}>
        <li> Is your phone turned on and within signal range?</li>
        <li>Is your number entered correctly?</li>
      </Typography>
    </Box>
    <Typography fontWeight={'bold'}>Reminder:</Typography>
    <StyledTypography>
      If you are an existing patient, we may already have a phone number linked to your profile. For privacy and
      compliance reasons, we are required to use the number on file.
    </StyledTypography>
    <StyledTypography>
      If your number has changed, please book a consultation with a nurse to update your personal details.
    </StyledTypography>
  </>
);
const TechnicalIssueInfo = ({ attemptsRemaining }: { attemptsRemaining: number }) => (
  <>
    <ResendInfoHeading>We&apos;ve resent the SMS</ResendInfoHeading>
    <StyledTypography>
      If the code still hasn’t arrived after waiting a minute, it may be due to a technical issue.
    </StyledTypography>
    <StyledTypography>
      We recommend contacting support so we can verify your details and help you complete the process.
    </StyledTypography>
    <StyledTypography>
      {`You have ${attemptsRemaining} attempt${attemptsRemaining !== 1 ? 's' : ''} remaining before you’ll need to wait 1 hour to try again.`}
    </StyledTypography>
  </>
);

const MaximumAttemptsReachedInfo = () => (
  <>
    <ResendInfoHeading>Maximum attempts reached</ResendInfoHeading>
    <StyledTypography>You’ve reached the maximum number of resend attempts.</StyledTypography>
    <StyledTypography>To keep your account secure, we’ve paused further attempts for now.</StyledTypography>
    <StyledTypography>Please contact support for assistance — or you can try again in 1 hour.</StyledTypography>
  </>
);

const renderSmsRetryInfo = (sendSmsRetryCount: number, blocked = false) => {
  if (blocked) {
    return <MaximumAttemptsReachedInfo />;
  }

  switch (sendSmsRetryCount) {
    case 0:
      return <InitialRetryInfo />;
    case 1:
      return <SecondRetryInfo />;
    case 2:
      return <TechnicalIssueInfo attemptsRemaining={2} />;
    case 3:
      return <TechnicalIssueInfo attemptsRemaining={1} />;
    case 4:
      return <MaximumAttemptsReachedInfo />;
    default:
      return <InitialRetryInfo />;
  }
};

const ResendSmsInfo = ({ dialogInfoIndex, sendSmsRetryCount, setSendSmsRetryCount }: ResendSmsInfoProps) => {
  const { blocked } = useSmsVerificationBlocked({
    sendSmsRetryCount,
  });

  if (blocked) {
    setSendSmsRetryCount(0);
  }

  return <ResendSmsInfoSection>{renderSmsRetryInfo(dialogInfoIndex, blocked)}</ResendSmsInfoSection>;
};

export default ResendSmsInfo;
